import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
} from "@mui/material";
import { formatDate } from "../utils/date";
import api from "../Services/http";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import {
    detachReportEmail,
    getAllReports,
    getReportEmails,
    transferReportEmail,
} from "../Services/sauvegardesService";

function extractReadableBody(rawBody) {
    if (!rawBody) return "";

    const looksLikeHtml = /<\s*[a-z][^>]*>/i.test(rawBody);
    if (!looksLikeHtml) return rawBody;

    const normalizedHtml = rawBody
        .replace(/<\s*br\s*\/?>/gi, "\n")
        .replace(/<\/\s*(p|div|h[1-6]|li|tr)\s*>/gi, "\n")
        .replace(/<\s*li[^>]*>/gi, "- ");

    const parser = new DOMParser();
    const doc = parser.parseFromString(normalizedHtml, "text/html");
    const extractedText = doc.body.textContent || "";

    const cleanedText = extractedText
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

    return cleanedText || rawBody;
}

export default function EmailDetailModal({ open, onClose, jobId, jobName, reportData, reportId, onUpdated }) {
    const [loading, setLoading] = useState(false);
    const [emailData, setEmailData] = useState(null);
    const [associatedEmails, setAssociatedEmails] = useState([]);
    const [availableReports, setAvailableReports] = useState([]);
    const [targetReportByEmail, setTargetReportByEmail] = useState({});
    const [actionLoadingByEmail, setActionLoadingByEmail] = useState({});
    const [error, setError] = useState(null);

    const loadModalData = async () => {
        setLoading(true);
        setError(null);
        try {
            let currentReport = reportData ?? null;
            if ((!currentReport || !currentReport.id) && reportId) {
                const oneReportResponse = await api.get(`/api/email-report-statuses/${reportId}`);
                currentReport = oneReportResponse.data?.data ?? null;
            }
            if (!currentReport) {
                // Ne PAS retomber sur le "latest by job" pour éviter d'afficher hier au lieu d'aujourd'hui.
                currentReport = {
                    id: null,
                    statusType: { name: "WARNING" },
                    summary: null,
                    checkedAt: null,
                    lastEmail: null,
                };
            }

            if (!currentReport?.id) {
                setEmailData(currentReport);
                setAssociatedEmails([]);
                return;
            }

            const [emails, reportsRes] = await Promise.all([
                getReportEmails(currentReport.id),
                getAllReports({ limit: 200 }),
            ]);
            const reports = Array.isArray(reportsRes?.data) ? reportsRes.data : [];

            setEmailData(currentReport);
            setAssociatedEmails(emails);
            setAvailableReports(reports.filter((r) => r.id !== currentReport.id));
        } catch (err) {
            console.error("Error loading email details:", err);
            setError("Impossible de charger les détails des emails");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && jobId) {
            loadModalData();
        } else {
            setEmailData(null);
            setAssociatedEmails([]);
            setError(null);
        }
    }, [open, jobId, reportData, reportId]);

    const runEmailAction = async (emailId, runner) => {
        setActionLoadingByEmail((prev) => ({ ...prev, [emailId]: true }));
        try {
            await runner();
            await loadModalData();
            if (typeof onUpdated === "function") {
                await onUpdated();
            }
        } catch (err) {
            console.error("Email action error:", err);
            setError(err?.response?.data?.message || "Action impossible sur cet email");
        } finally {
            setActionLoadingByEmail((prev) => ({ ...prev, [emailId]: false }));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pb: 2 }}>Emails associés - {jobName}</DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={4}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Alert severity="error">{error}</Alert>
                )}

                {!loading && !error && emailData && (
                    <Box>
                        {/* Statut */}
                        <Box mb={2}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                État de la sauvegarde
                            </Typography>
                            <Chip
                                label={emailData.statusType?.name || "UNKNOWN"}
                                color={
                                    emailData.statusType?.name === "OK"
                                        ? "success"
                                        : emailData.statusType?.name ===
                                            "FAILED"
                                          ? "error"
                                          : emailData.statusType?.name ===
                                              "WARNING"
                                            ? "warning"
                                            : "default"
                                }
                                size="small"
                                sx={{ mt: 0.5 }}
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {emailData.lastEmail && (
                            <>
                                {/* Infos email */}
                                <Box mb={2}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        De
                                    </Typography>
                                    <Typography variant="body1">
                                        {emailData.lastEmail.from}
                                    </Typography>
                                </Box>

                                <Box mb={2}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        À
                                    </Typography>
                                    <Typography variant="body1">
                                        {emailData.lastEmail.to}
                                    </Typography>
                                </Box>

                                <Box mb={2}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Date de réception
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDate(emailData.lastEmail.dateReceived)}
                                    </Typography>
                                </Box>

                                <Box mb={2}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Sujet
                                    </Typography>
                                    <Typography variant="body1" fontWeight="medium">
                                        {emailData.lastEmail.subject}
                                    </Typography>
                                </Box>

                                {emailData.lastEmail.body && (
                                    <Box mb={2}>
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Corps du message
                                        </Typography>
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 2,
                                                bgcolor: "grey.100",
                                                borderRadius: 1,
                                                maxHeight: 300,
                                                overflow: "auto",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                component="pre"
                                                sx={{
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                    fontFamily: "inherit",
                                                }}
                                            >
                                                {extractReadableBody(emailData.lastEmail.body)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}

                        {emailData.summary && (
                            <Box mb={2}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Résumé
                                </Typography>
                                <Typography variant="body2">
                                    {emailData.summary}
                                </Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Tous les emails associés ({associatedEmails.length})
                        </Typography>
                        {associatedEmails.length === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Aucun email n'est associé à ce report actif.
                                {emailData.summary ? (
                                    <>
                                        {" "}
                                        D'après le résumé: <b>{emailData.summary}</b>.
                                    </>
                                ) : null}
                            </Alert>
                        ) : (
                            <List dense>
                                {associatedEmails.map((email) => (
                                    <ListItem key={email.id} divider sx={{ pr: 34 }}>
                                        <ListItemText
                                            primary={email.subject || "(Sans sujet)"}
                                            secondary={`${email.from} • ${formatDate(email.dateReceived)}`}
                                        />
                                        <ListItemSecondaryAction sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                                <InputLabel id={`target-report-${email.id}`}>Report cible</InputLabel>
                                                <Select
                                                    labelId={`target-report-${email.id}`}
                                                    value={targetReportByEmail[email.id] || ""}
                                                    onChange={(evt) =>
                                                        setTargetReportByEmail((prev) => ({
                                                            ...prev,
                                                            [email.id]: evt.target.value,
                                                        }))
                                                    }
                                                    input={<OutlinedInput label="Report cible" />}
                                                >
                                                    <MenuItem value="">
                                                        <em>Sélectionner</em>
                                                    </MenuItem>
                                                    {availableReports.map((report) => (
                                                        <MenuItem key={report.id} value={report.id}>
                                                            #{report.id} - {report.jobName} ({formatDate(report.checkedAt)})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    runEmailAction(email.id, () => detachReportEmail(emailData.id, email.id))
                                                }
                                                disabled={Boolean(actionLoadingByEmail[email.id])}
                                                title="Désassocier cet email"
                                            >
                                                <LinkOffIcon />
                                            </IconButton>
                                            <IconButton
                                                color="primary"
                                                onClick={() => {
                                                    const targetReportId = Number(targetReportByEmail[email.id] || 0);
                                                    if (!targetReportId) {
                                                        setError("Veuillez sélectionner un report cible avant transfert.");
                                                        return;
                                                    }
                                                    runEmailAction(email.id, () =>
                                                        transferReportEmail(emailData.id, email.id, targetReportId)
                                                    );
                                                }}
                                                disabled={Boolean(actionLoadingByEmail[email.id])}
                                                title="Transférer cet email"
                                            >
                                                <SwapHorizIcon />
                                            </IconButton>
                                            {actionLoadingByEmail[email.id] && <CircularProgress size={18} />}
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                            >
                                Dernière vérification
                            </Typography>
                            <Typography variant="body2">
                                {formatDate(emailData.checkedAt)}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {!loading && !error && !emailData && (
                    <Typography color="text.secondary" align="center">
                        Aucun email trouvé pour cette sauvegarde
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
}
